import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MicIcon from "@mui/icons-material/Mic";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import { COLORS } from "./constants";
import { commonInputProps, dangerButtonSx, inputSx, primaryButtonSx } from "./styles";

// ─────────────────────────────────────────────────────────────────────────────
// One field reporter on the control-room desk (backend-/PLAN.md): one line with
// what the control room needs during a show — who it is, whether they're on
// air and hear you, Hold to talk, Go live / Standby. The QR code, the vMix
// fields and the rest open in the setup window (ReporterSetupDialog) with ⚙.
// ─────────────────────────────────────────────────────────────────────────────

// 9:45, or 2:59:50 once it is an hour or more.
const formatCountdown = (secs) => {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = String(secs % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

function statusOf(ch) {
  if (ch.failure) return { label: "Stopped: error", color: COLORS.red };
  if (!ch.live) return { label: "Standby", color: COLORS.muted };
  if (ch.starting || ch.relinking) return { label: "Starting…", color: COLORS.amber };
  if (ch.cameraConnected) {
    const level = ch.cameraLoss?.level;
    return { label: level === "bad" ? "On air · weak link" : "On air", color: level === "bad" ? COLORS.amber : COLORS.green };
  }
  return { label: "Waiting for the phone", color: COLORS.amber };
}

// A disabled button must still read as a button (Hold to talk before the mic is on,
// Go live at the live limit), not vanish into the dark card.
const disabledSx = { "&.Mui-disabled": { bgcolor: "rgba(239,227,204,0.06)", color: COLORS.muted, border: `1px solid ${COLORS.border}` } };

function Chip({ children, color }) {
  return (
    <Box component="span" sx={{
      px: 0.9, py: 0.2, borderRadius: "9px", fontFamily: COLORS.fontSans, fontSize: 11, fontWeight: 700, color,
      border: `1px solid ${color}55`, bgcolor: `${color}14`, whiteSpace: "nowrap"
    }}>{children}</Box>
  );
}

export function ReporterCard({ channel: ch, busy, canGoLive, talk, onGoLive, onStandby, onPatch, onSetup }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(ch.name);
  useEffect(() => { if (!editing) setName(ch.name); }, [ch.name, editing]);

  const status = statusOf(ch);
  const n = ch.channel;
  const talkingHere = talk.target === n && talk.active;

  const saveName = () => {
    setEditing(false);
    if (name.trim() && name !== ch.name) onPatch(n, { name });
    else setName(ch.name);
  };

  const holdProps = {
    onPointerDown: (e) => { e.currentTarget.setPointerCapture?.(e.pointerId); talk.start(n); },
    onPointerUp: () => talk.stop(),
    onPointerCancel: () => talk.stop(),
    onLostPointerCapture: () => talk.stop(),
    onKeyDown: (e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); talk.start(n); } },
    onKeyUp: (e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); talk.stop(); } },
    onContextMenu: (e) => e.preventDefault()
  };

  const notes = [
    ch.failure && { role: "alert", color: "#E8A0A0", text: `${ch.failure}. Press Go live to try again (same QR code).` },
    ch.securityEvent && { role: "status", color: "#E0C68F", text: ch.securityEvent.message },
    ch.return?.securityEvent && { role: "status", color: "#E0C68F", text: ch.return.securityEvent.message },
    !ch.live && ch.lastStop?.message && { color: COLORS.muted, text: ch.lastStop.message }
  ].filter(Boolean);

  return (
    <Box sx={{
      px: 1.5, py: 1.1, borderRadius: "12px",
      bgcolor: ch.live ? `${status.color}0D` : COLORS.panelSoft,
      border: `1px solid ${ch.live ? `${status.color}55` : COLORS.border}`
    }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 1.5 }} alignItems={{ sm: "center" }}>
        {/* Who and how */}
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, bgcolor: status.color,
            animation: ch.cameraConnected ? "pulse 1.4s infinite" : "none" }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {editing ? (
              <TextField {...commonInputProps} autoFocus value={name} onChange={(e) => setName(e.target.value)}
                onBlur={saveName} onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setName(ch.name); setEditing(false); } }}
                inputProps={{ maxLength: 40, "aria-label": `Name of channel ${n}` }} sx={{ ...inputSx, maxWidth: 260 }} />
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography sx={{ color: COLORS.text, fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ch.name}
                </Typography>
                <Box component="button" type="button" onClick={() => setEditing(true)} aria-label={`Rename ${ch.name}`}
                  sx={{ all: "unset", cursor: "pointer", display: "flex", color: COLORS.muted, opacity: 0.7,
                    "&:hover, &:focus-visible": { color: COLORS.text, opacity: 1 } }}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </Box>
              </Stack>
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
              <Typography sx={{ color: status.color, fontSize: 12.5, fontWeight: 600 }}>{status.label}</Typography>
              {ch.return?.readerConnected && <Chip color={COLORS.cyan}>Hears you</Chip>}
              {ch.return?.talking && <Chip color={COLORS.amber}>Talking</Chip>}
              {ch.live && !ch.cameraConnected && ch.idleClosesIn != null && (
                <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>· standby in {formatCountdown(ch.idleClosesIn)}</Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* What the control room does */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-end", sm: "flex-start" }} sx={{ flexShrink: 0 }}>
          {ch.live && (
            <Button size="small" variant="contained" startIcon={<MicIcon />} disabled={!talk.ready}
              aria-pressed={talkingHere} {...holdProps}
              title={talk.ready ? "" : "Turn on the talkback mic first"}
              sx={{ ...primaryButtonSx, ...disabledSx, userSelect: "none", touchAction: "none", whiteSpace: "nowrap",
                ...(talkingHere ? { bgcolor: `${COLORS.amber} !important`, color: `${COLORS.onAccent} !important` } : {}) }}>
              {talkingHere ? "Talking…" : "Hold to talk"}
            </Button>
          )}
          {ch.live ? (
            <Button size="small" variant="contained" startIcon={<PauseCircleIcon />} onClick={() => onStandby(n)}
              disabled={busy} sx={{ ...dangerButtonSx, ...disabledSx, whiteSpace: "nowrap" }}>
              Standby
            </Button>
          ) : (
            <Button size="small" variant="contained" startIcon={busy ? null : <PlayArrowIcon />} onClick={() => onGoLive(n)}
              disabled={busy || !canGoLive} title={canGoLive ? "" : "The live limit is reached: put a reporter on standby first"}
              sx={{ ...primaryButtonSx, ...disabledSx, minWidth: 92, whiteSpace: "nowrap" }}>
              {busy ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Go live"}
            </Button>
          )}
          <Tooltip title="Set up: QR code, vMix">
            <IconButton onClick={() => onSetup(n)} aria-label={`Set up ${ch.name}`} aria-haspopup="dialog"
              sx={{ color: COLORS.cyan, border: "1px solid rgba(43,189,175,0.42)", borderRadius: "11px", width: 34, height: 32,
                "&:hover": { bgcolor: "rgba(43,189,175,0.10)" } }}>
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {notes.map((m, i) => (
        <Typography key={i} role={m.role} sx={{ mt: 0.75, pl: 3, color: m.color, fontSize: 12, lineHeight: 1.5 }}>{m.text}</Typography>
      ))}
    </Box>
  );
}
